import { changeFocus, createElemPropsHook, getFirstFocusableElement } from '@workday/canvas-kit-react/common';
import { usePopupModel } from '@workday/canvas-kit-react/popup';
import React from 'react';

/**
 * A local copy of Canvas useInitialFocus that gracefully handles cases where no focusable element is found.
 * Unlike the original useInitialFocus, this version will not throw an error if there's no focusable element.
 * It will still attempt to focus the initial element or first focusable element if they exist.
 *
 * This hook is useful for modals or popups where the content might not always have focusable elements,
 * preventing runtime errors while maintaining accessibility when focusable elements are present.
 */
export const useOptionalInitialFocus = createElemPropsHook(usePopupModel)((model: ReturnType<typeof usePopupModel>) => {
  const visible = model.state.visibility !== 'hidden';

  // Using `useEffect` instead of `useLayoutEffect` so that focus doesn't change _before_ PopperJS
  // has positioned the Popup. If we change focus before positioning, the browser will scroll to the
  // top of the page.
  React.useEffect(() => {
    if (visible && model.state.stackRef.current) {
      const element = model.state.initialFocusRef?.current || getFirstFocusableElement(model.state.stackRef.current);

      // Only attempt to focus if an element was found - no assertion/error throwing
      if (element) {
        requestAnimationFrame(() => {
          changeFocus(element);
        });
      }
    }
  }, [model.state.initialFocusRef, model.state.stackRef, visible]);

  return {};
});
